import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { IEmployeesPayload, IInitialStateEmployees, ISetStructureTypePayload } from "../types";
import { GetDeleteEmployees, GetEmployees } from "./thunk";
import { ITranslation } from "../../../../types";

const initialState: IInitialStateEmployees = {
    employees: [],
    employeesRestore: [],
    selectedEmployeesID: [],
    isLoadingEmployees: true,
    isViewEmployeesRestore: false,
    employeesWork: [],
    structureType: {},
    employeeActivePage: "",
    employeesColumnsKeys: [],
    employeesCheckedColumns: [],
    employeesID: 0,
    employeesUsers: [],
};

const employeesSlice = createSlice({
    name: "employees",
    initialState,
    reducers: {
        setEmployees: (
            state,
            action: PayloadAction<{
                data: IEmployeesPayload[];
                defaultLanguage: string;
            }>
        ) => {
            const { data, defaultLanguage } = action.payload;

            state.employees = data;

            state.employeesUsers = data.map(employee => {
                const translation = employee.translations.find(t => t.slug === defaultLanguage);
                return {
                    id: employee.id,
                    first_name: translation?.first_name || "",
                    last_name: translation?.last_name || "",
                };
            });
        },

        setEmployeesWork: (state, action) => {
            state.employeesWork = action.payload;
        },
        setEmployeesRestore: (state, action) => {
            state.employeesRestore = action.payload;
        },
        setIsViewEmployeesRestore: state => {
            state.isViewEmployeesRestore = !state.isViewEmployeesRestore;
        },
        deletedRestoreEmployees: (state, action) => {
            state.employeesRestore = state.employeesRestore.filter(elem => elem.id !== action.payload);
        },
        setEmployeesID: (state, action) => {
            state.employeesID = action.payload;
        },

        setEmployeesCheckedColumns: (state, action) => {
            state.employeesCheckedColumns = action.payload;
        },
        setStructureType: (
            state,
            action: PayloadAction<{
                roleIndex: string | number;
                data: ISetStructureTypePayload[];
                defaultLanguage: string;
            }>
        ) => {
            const { roleIndex, data, defaultLanguage } = action.payload;
            if (!Array.isArray(data)) {
                return;
            }
            if (!state.structureType || typeof state.structureType !== "object") {
                state.structureType = {};
            }
            const index = typeof roleIndex === "number" ? roleIndex : 100;
            state.structureType[index] = [];
            const filteredData = data.flatMap(item =>
                item.translations
                    .filter((elem: ITranslation) => elem.slug === defaultLanguage)
                    .map((elem: ITranslation) => ({
                        id: item.id,
                        name: elem.name || elem.faculty || "",
                    }))
            );
            const uniqueData = new Map(filteredData.map(item => [item.id, item]));
            state.structureType[index] = Array.from(uniqueData.values());
        },
        removeStructureType: (state, action: PayloadAction<string | number>) => {
            const roleIndex = action.payload;
            const index = typeof roleIndex === "number" ? roleIndex : 100;
            if (state.structureType && typeof state.structureType === "object") {
                delete state.structureType[index];
            }
        },

        setSelectedEmployees: (state, action) => {
            state.selectedEmployeesID = action.payload;
        },
        deleteEmployee: (state, action: PayloadAction<number>) => {
            const id = action.payload;
            state.employees = state.employees.filter(elem => elem.id !== id);
        },

        deleteItemEmployee: (state, action: PayloadAction<number>) => {
            state.selectedEmployeesID = state.selectedEmployeesID.filter(id => id !== action.payload);
        },
        setEmployeeActivePage: (state, action: PayloadAction<string>) => {
            state.employeeActivePage = action.payload;
        },
    },
    extraReducers: builder => {
        builder
            .addCase(GetEmployees.pending, state => {
                state.isLoadingEmployees = true;
            })
            .addCase(GetEmployees.fulfilled, state => {
                state.isLoadingEmployees = false;
            })
            .addCase(GetEmployees.rejected, state => {
                state.isLoadingEmployees = false;
            })
            .addCase(GetDeleteEmployees.pending, state => {
                state.isLoadingEmployees = true;
            })
            .addCase(GetDeleteEmployees.fulfilled, state => {
                state.isLoadingEmployees = false;
            })
            .addCase(GetDeleteEmployees.rejected, state => {
                state.isLoadingEmployees = false;
            });
    },
});

export const {
    setEmployees,
    deleteEmployee,
    setSelectedEmployees,
    setEmployeesWork,
    deleteItemEmployee,
    setEmployeesRestore,
    setIsViewEmployeesRestore,
    deletedRestoreEmployees,
    setStructureType,
    setEmployeeActivePage,
    removeStructureType,
    setEmployeesID,
    setEmployeesCheckedColumns,
} = employeesSlice.actions;
export default employeesSlice;
